import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../contexts/DataContext';
import KPICard from '../../components/reports/KPICard';
import ReportChart from '../../components/reports/ReportChart';
import ReportTable from '../../components/reports/ReportTable';
import BranchSelect from '../../components/reports/BranchSelect';
import { getIndicatorCategoryDistribution, getUniqueBranches, filterByBranch, exportToCSV } from '../../utils/reportHelpers';

function ModelsReport() {
  const navigate = useNavigate();
  const { models, warnings, indicators, customers } = useData();
  
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const branches = getUniqueBranches(customers);
  
  // Filter warnings by branch first
  const filteredWarnings = filterByBranch(warnings, customers, selectedBranch);

  // Model performance
  const modelPerformance = models.map(model => {
    const modelWarnings = filteredWarnings.filter(w => w.modelId === model.id);
    return {
      id: model.id,
      name: model.name,
      code: model.code,
      warningCount: modelWarnings.length,
      highRisk: modelWarnings.filter(w => w.riskLevel === 'high').length,
      mediumRisk: modelWarnings.filter(w => w.riskLevel === 'medium').length,
      lowRisk: modelWarnings.filter(w => w.riskLevel === 'low').length,
      avgScore: modelWarnings.length > 0 
        ? (modelWarnings.reduce((sum, w) => sum + w.score, 0) / modelWarnings.length).toFixed(1)
        : 0
    };
  });

  const modelPerfChart = {
    labels: modelPerformance.slice(0, 10).map(m => m.code),
    datasets: [
      {
        label: 'Cao',
        data: modelPerformance.slice(0, 10).map(m => m.highRisk),
        backgroundColor: '#dc3545'
      },
      {
        label: 'Trung bình',
        data: modelPerformance.slice(0, 10).map(m => m.mediumRisk),
        backgroundColor: '#fd7e14'
      },
      {
        label: 'Thấp',
        data: modelPerformance.slice(0, 10).map(m => m.lowRisk),
        backgroundColor: '#ffc107'
      }
    ]
  };

  const indicatorDistribution = getIndicatorCategoryDistribution(indicators);

  const tableColumns = [
    { key: 'code', label: 'Mã mô hình', sortable: true },
    { key: 'name', label: 'Tên mô hình', sortable: true },
    { key: 'targetObject', label: 'Đối tượng' },
    { 
      key: 'indicators', 
      label: 'Số chỉ tiêu',
      render: (val) => val?.length || 0
    },
    { 
      key: 'assignedCustomers', 
      label: 'Số KH',
      render: (val) => val?.length || 0
    },
    { key: 'status', label: 'Trạng thái' },
    {
      key: 'actions',
      label: 'Hành động',
      render: (_, row) => (
        <button 
          className="btn btn-sm btn-outline-primary"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedModel(row);
          }}
        >
          <i className="bi bi-eye"></i>
        </button>
      )
    }
  ];

  const handleExport = () => {
    const exportData = models.map(m => ({
      'Mã': m.code,
      'Tên': m.name,
      'Đối tượng': m.targetObject,
      'Số indicators': m.indicators?.length || 0,
      'Số KH được gán': m.assignedCustomers?.length || 0,
      'Trạng thái': m.status,
      'Phương pháp tính': m.calculationMethod,
      'Ngày tạo': m.createdDate
    }));
    exportToCSV(exportData, 'bao-cao-mo-hinh');
  };

  return (
    <div className="container-fluid">
      <div className="page-header-sticky">
        <div className="row mb-4">
          <div className="col-12">
            <button className="btn btn-outline-secondary mb-3" onClick={() => navigate('/reports')}>
              <i className="bi bi-arrow-left me-2"></i>
              Quay lại
            </button>
            <h2>
              <i className="bi bi-diagram-3 me-2 text-primary"></i>
              Báo cáo các mô hình cảnh báo sớm
            </h2>
            <p className="text-muted">Model performance monitoring và tuning insights</p>
          </div>
        </div>
      </div>

      {/* Branch Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          <BranchSelect 
            value={selectedBranch}
            onChange={setSelectedBranch}
            branches={branches}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-3">
          <KPICard 
            title="Tổng số mô hình"
            value={models.length}
            icon="bi-diagram-3"
            color="primary"
            subtitle={`${models.filter(m => m.status === 'active').length} active`}
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Tổng chỉ tiêu"
            value={indicators.length}
            icon="bi-list-check"
            color="info"
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Warnings phát sinh"
            value={filteredWarnings.length}
            icon="bi-bell"
            color="warning"
            subtitle={(!selectedBranch || selectedBranch.length === 0 || selectedBranch.includes('all')) 
              ? 'Từ tất cả mô hình' 
              : `${selectedBranch.length} chi nhánh được chọn`}
          />
        </div>
        <div className="col-md-3">
          <KPICard 
            title="Mô hình hiệu quả nhất"
            value={modelPerformance.sort((a, b) => b.warningCount - a.warningCount)[0]?.code || '-'}
            icon="bi-trophy"
            color="success"
          />
        </div>
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4 stagger-animation">
        <div className="col-md-8">
          <ReportChart 
            type="bar"
            title="Performance Comparison (Top 10 models)"
            data={modelPerfChart}
            height={300}
            options={{
              plugins: { legend: { position: 'bottom' } },
              scales: { 
                x: { 
                  stacked: true,
                  ticks: {
                    maxRotation: 0,
                    minRotation: 0,
                    autoSkip: false,
                    font: { size: 11 }
                  }
                }, 
                y: { stacked: true, beginAtZero: true } 
              }
            }}
          />
        </div>
        <div className="col-md-4">
          <ReportChart 
            type="doughnut"
            title="Phân bố indicators theo category"
            data={indicatorDistribution}
            height={300}
          />
        </div>
      </div>

      {/* Model Inventory Table */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>Model Inventory ({models.length} bản ghi)</h4>
            <button className="btn btn-primary" onClick={handleExport}>
              <i className="bi bi-download me-2"></i>
              Xuất Excel
            </button>
          </div>
          <ReportTable 
            data={models}
            columns={tableColumns}
            onRowClick={(row) => navigate('/models', { state: { selectedModel: row } })}
            pageSize={15}
          />
        </div>
      </div>

      {/* Model Detail Modal */}
      {selectedModel && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-diagram-3 me-2"></i>
                  {selectedModel.name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedModel(null)}
                ></button>
              </div>
              <div className="modal-body">
                {/* Meta Info */}
                <div className="card mb-3">
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p><strong>Mã mô hình:</strong> {selectedModel.code}</p>
                        <p><strong>Đối tượng:</strong> {selectedModel.targetObject}</p>
                        <p><strong>Trạng thái:</strong> <span className={`badge bg-${selectedModel.status === 'active' ? 'success' : 'secondary'}`}>{selectedModel.status}</span></p>
                      </div>
                      <div className="col-md-6">
                        <p><strong>Phương pháp:</strong> {selectedModel.calculationMethod}</p>
                        <p><strong>Ngày tạo:</strong> {selectedModel.createdDate}</p>
                        <p><strong>Số KH:</strong> {selectedModel.assignedCustomers?.length || 0}</p>
                      </div>
                    </div>
                    <p className="mb-0"><strong>Mô tả:</strong> {selectedModel.description}</p>
                  </div>
                </div>

                {/* Indicators */}
                <div className="card mb-3">
                  <div className="card-header bg-primary text-white">
                    <strong>Danh sách chỉ tiêu ({selectedModel.indicators?.length || 0})</strong>
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Mã</th>
                          <th>Tên</th>
                          <th>Trọng số</th>
                          <th>Tần suất</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedModel.indicators?.map((ind, idx) => {
                          const fullIndicator = indicators.find(i => i.id === ind.id);
                          return (
                            <tr key={idx}>
                              <td><code>{ind.id}</code></td>
                              <td>{fullIndicator?.name || ind.id}</td>
                              <td><span className="badge bg-info">{(ind.weight * 100).toFixed(0)}%</span></td>
                              <td><small>{ind.frequency}</small></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Thresholds */}
                <div className="card">
                  <div className="card-header bg-warning">
                    <strong>Ngưỡng cảnh báo</strong>
                  </div>
                  <div className="card-body">
                    {selectedModel.thresholds && (
                      <div>
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge bg-danger me-2" style={{ width: '80px' }}>Cao</span>
                          <span>≥ {selectedModel.thresholds.high?.min}</span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge bg-warning me-2" style={{ width: '80px' }}>Trung bình</span>
                          <span>{selectedModel.thresholds.medium?.min} - {selectedModel.thresholds.medium?.max}</span>
                        </div>
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge bg-success me-2" style={{ width: '80px' }}>Thấp</span>
                          <span>{selectedModel.thresholds.low?.min} - {selectedModel.thresholds.low?.max}</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <span className="badge bg-secondary me-2" style={{ width: '80px' }}>Không</span>
                          <span>&lt; {selectedModel.thresholds.none?.max}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => {
                    navigate('/models', { state: { selectedModel } });
                    setSelectedModel(null);
                  }}
                >
                  Chỉnh sửa
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setSelectedModel(null)}
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModelsReport;
